import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { OrgUnitReferenceDTO, PersonRichReadDTO, TagReadDTO } from '../../api/dto';
import { createTag } from '../../api/tags';
import { useApplicationContext } from '../../components/application/ApplicationContextProvider';
import { FilterTypeEnum } from '../../components/searching/filters';
import { fetchTagsByName } from '../../lib/api';
import { FetchUseCase } from '../fields/AsyncAutocompleteElement';
import { AsyncAutocompleteFormElement, TFilterElement, TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';

enum TagTarget {
    Department = 'department',
    Group = 'group',
    Section = 'section',
}

type TagTargetConfig = {
    value: TagTarget;
    getLabel: (currentUser: PersonRichReadDTO) => string | null | undefined;
};

const TAG_TARGETS: TagTargetConfig[] = [
    { value: TagTarget.Department, getLabel: (currentUser: PersonRichReadDTO) => currentUser.department?.code },
    { value: TagTarget.Group, getLabel: (currentUser: PersonRichReadDTO) => currentUser.group?.code },
    { value: TagTarget.Section, getLabel: (currentUser: PersonRichReadDTO) => currentUser.section?.code },
];

interface CreateNewTagComponentProps {
    activityId?: number;
    refetchActivityTags?: () => void;
}

const CreateNewTagComponent =
    ({ activityId, refetchActivityTags }: CreateNewTagComponentProps) =>
    (inputValue: string | undefined) => {
        const intl = useIntl();
        const { reset, setValue } = useFormContext();
        const { currentUser } = useApplicationContext();

        const getOrgUnit = (orgUnitTarget: TagTarget) => {
            switch (orgUnitTarget) {
                case TagTarget.Department:
                    return currentUser.department;
                case TagTarget.Group:
                    return currentUser.group;
                case TagTarget.Section:
                    return currentUser.section;
                default: {
                    return null;
                }
            }
        };

        const { mutate: mutateCreateTag, isLoading } = useMutation(
            async (data: { name: string; orgUnit: OrgUnitReferenceDTO }) => {
                const createdTag = await createTag({ activityId, data });
                if (activityId) {
                    refetchActivityTags?.();
                }
                return createdTag;
            },
            {
                onSuccess: (createdTag) => {
                    if (activityId) {
                        reset({ newTags: [] });
                    } else {
                        setValue('tag', createdTag, { shouldValidate: true, shouldDirty: true });
                        (document.activeElement as HTMLElement)?.blur();
                    }
                },
                onError: () => toast.error(intl.formatMessage({ id: 'document.tags.error' })),
            }
        );

        const handleCreate = (orgUnitTarget: TagTarget) => {
            const name = inputValue;
            const orgUnit = getOrgUnit(orgUnitTarget);
            if (!name || !orgUnit) {
                return toast.error(intl.formatMessage({ id: 'document.tags.error' })); // Shouldn't happen
            }
            mutateCreateTag({ name, orgUnit });
        };

        if (isLoading) return <LinearProgress />;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                        id="document.tags.createFor"
                        values={{
                            inputValue,
                            strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                        }}
                    />
                </Typography>

                <Stack direction="row" spacing={1.5}>
                    {TAG_TARGETS.map((t) => {
                        const label = t.getLabel(currentUser);
                        if (!label) return <></>;
                        return (
                            <Button
                                key={t.value}
                                size="small"
                                variant="outlined"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleCreate(t.value)}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Stack>
            </Box>
        );
    };

const tagTypeProperties = (
    onlyTaggable: boolean,
    extraProps?: CreateNewTagComponentProps
): AsyncAutocompleteFormElement['typeProperties'] => ({
    idField: 'id',
    fetchOptions: (searchTerm) => fetchTagsByName(searchTerm, onlyTaggable),
    fetchUseCases: [FetchUseCase.ON_OPEN, FetchUseCase.ON_KEYSTROKE],
    getOptionsDisabled: onlyTaggable ? (option: TagReadDTO) => option.source === 'OIM' : undefined,
    getOptionLabel: (value: TagReadDTO) => value.displayName,
    multiple: false,
    noOptionsText: extraProps ? CreateNewTagComponent(extraProps) : undefined,
});
interface TagSchemaParameters extends Pick<AsyncAutocompleteFormElement, 'path' | 'label' | 'required'> {
    extraProps?: CreateNewTagComponentProps;
}

export const tagSchema = ({ path, label, required, extraProps }: TagSchemaParameters): TFormElement => ({
    type: UiSchemaType.AUTOCOMPLETE_ASYNC,
    path,
    label,
    required,
    typeProperties: tagTypeProperties(true, extraProps),
});

interface TagFilterSchemaParameters extends Pick<TFilterElement, 'id' | 'label'> {}

export const tagFilterSchema = ({ id, label }: TagFilterSchemaParameters): TFilterElement => ({
    type: FilterTypeEnum.AUTOCOMPLETE_ASYNC,
    id,
    label,
    valueGetter: (value: TagReadDTO) => value.id,
    typeProperties: tagTypeProperties(false),
});
